/**
 * PlatformPayButton: renders the Stripe Express Checkout Element (Apple Pay /
 * Google Pay) inside its OWN Elements instance so the site's card Elements stay
 * untouched. Owns the confirm → PaymentIntent → confirmPayment sequence and hands
 * the resulting paymentIntentId back to the caller to complete the booking.
 * Reports wallet availability up so the payment dropdown can gate the option.
 * Failures call onError(message, stage) where stage is 'payment' (failed
 * before/at wallet authorization) or 'api' (booking failed after the wallet
 * authorized) — the RN app's Booking Failed errorType vocabulary.
 */
import React, { useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { createPaymentIntent, useMe } from '../../services/GraphQlClientService'
import { mq } from '../../helpers'

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_ELEMENTS_API_KEY)

// Express Checkout Element create-time options.
//
// Verified against Stripe's live "Create the Express Checkout Element" reference
// (docs.stripe.com/js/elements_object/create_express_checkout_element, fetched
// 2026-08-12): `paymentMethods` documents all six sub-keys — `applePay`,
// `googlePay`, `link`, `paypal`, `amazonPay`, `klarna` — each `'auto'|'always'|'never'`,
// with "If you don't want to show a given payment method as a payment option, set
// its property in `paymentMethods` to `never`." `emailRequired` is documented
// alongside it, and the same reference's React sample confirms
// `<ExpressCheckoutElement options={{ emailRequired: true }} />`.
//
// NOTE: the npm-pinned `@stripe/stripe-js@1.54.2` type declarations
// (node_modules/@stripe/stripe-js/types/stripe-js/elements/express-checkout.d.ts,
// `StripeExpressCheckoutElementOptions`) don't declare `paymentMethods` or
// `emailRequired` at all (they only list an older `wallets` key covering
// applePay/googlePay). That's immaterial here: this is a plain `.js` file with no
// build-time type checking, and Stripe.js is always loaded live from Stripe's CDN
// by `loadStripe()` at runtime regardless of the pinned npm package version — so
// the evergreen runtime (and the live reference above) is what actually executes,
// not the bundled, stale `.d.ts`.
//
// Product decision: device wallets only. Link/PayPal/Amazon Pay/Klarna are
// explicitly excluded here (spec §3) rather than left to Dashboard-configured
// defaults, so this holds regardless of what any given Stripe account/payment
// method configuration has active.
const ECE_OPTIONS = {
  emailRequired: true,
  // Tallest the Express Checkout Element allows — Stripe caps buttonHeight at
  // 40–55px (default 44). 55 brings the wallet button closer to the "Book Now"
  // (ArrowButton) height. Width is always 100% of the mounted container.
  buttonHeight: 55,
  paymentMethods: {
    applePay: 'auto',
    googlePay: 'auto',
    link: 'never',
    paypal: 'never',
    amazonPay: 'never',
    klarna: 'never',
  },
}

const toCents = dollars => Math.round(dollars * 100)

// Wraps the Express Checkout Element so it sits like the footer "Book Now"
// (ArrowButton): on mobile it spans the width with a 16px inset on the sides and
// bottom (ArrowButton gets the same look from a 16px white border; an iframe can't
// be bordered that way, so we use padding); on desktop it matches ArrowButton's
// fixed width. While hidden for the availability probe it collapses to zero height
// with no inset, so it takes up no space in the footer.
const ButtonWrapper = styled.div`
  ${props =>
    props.visible
      ? `
    width: 361px;
    max-width: 100%;
    /* Vertical padding so the 55px wallet button occupies ~the same height as the
       80px "Book Now" (ArrowButton) on desktop — without it the fixed footer visibly
       shrinks when the button swaps in. */
    padding: 12px 0;
    /* Keep the button off the right edge on desktop (reset on mobile, where it's
       full-width and already inset by its own side padding). */
    margin-right: 16px;

    ${mq('max', '768px')} {
      box-sizing: border-box;
      width: 100%;
      padding: 0 16px 16px;
      margin-right: 0;
    }
  `
      : `
    height: 0;
    overflow: hidden;
  `}
`

const PlatformPayInner = ({
  amountDollars,
  visible,
  onAvailabilityChange,
  onConfirmBooking,
  onError,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const me = useMe() // cached app-wide; supplies the userId for the PaymentIntent
  // Synchronous re-entry guard: React state is too slow to stop a double-tap
  // opening two wallet sheets (RN parity: book-button.tsx bookInFlight).
  const inFlight = useRef(false)

  // Keep the wallet sheet total in sync as tip/promo change the price. `elements`
  // here is the Elements *group* object (useElements()), whose update() accepts
  // `amount` per StripeElementsUpdateOptions — distinct from the ExpressCheckoutElement
  // instance's own update(), which only takes buttonHeight/layout/paymentMethodOrder.
  useEffect(() => {
    if (elements) { elements.update({ amount: toCents(amountDollars) }) }
  }, [elements, amountDollars])

  const handleConfirm = async () => {
    if (!stripe || !elements || inFlight.current) { return }
    if (!me?.id) {
      onError('There was a problem completing your payment.', 'payment')
      return
    }
    inFlight.current = true
    // Failure-stage attribution for onError's second arg, using the RN app's
    // Booking Failed errorType vocabulary (mantis book-button.tsx): 'payment' =
    // failed before/at wallet authorization (no charge exists), 'api' =
    // bookAppointment failed AFTER confirmPayment authorized (an uncaptured
    // hold exists — the incident case analytics must be able to isolate).
    let stage = 'payment'
    try {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        onError(submitError.message, 'payment')
        return
      }

      const { id: paymentIntentId, clientSecret } = await createPaymentIntent(amountDollars)

      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        // /book is a real Next.js route (the booking flow fakes step URLs like
        // /book/6 via history.replaceState, so that path 404s if Stripe ever
        // actually navigates here). Wallet methods complete in-page under
        // `redirect: 'if_required'` below, so this URL is essentially never hit —
        // it only matters as a fallback if Stripe forces a redirect.
        confirmParams: { return_url: `${window.location.origin}/book` },
        redirect: 'if_required', // Apple/Google Pay complete in-page (spec §6)
      })
      if (error) {
        onError(error.message, 'payment')
        return
      }

      stage = 'api' // the wallet has authorized; anything that throws now failed post-payment
      await onConfirmBooking(paymentIntentId)
    } catch (err) {
      onError(err?.message || 'There was a problem completing your payment.', stage)
    } finally {
      inFlight.current = false
    }
  }

  return (
    // Mounted even when not selected so onReady can report availability; the wallet
    // button is only revealed when `visible` (ButtonWrapper collapses to height:0 while
    // hidden — NOT display:none, which would stop the ECE iframe from loading and
    // prevent onReady from ever firing). `inert` (React 19 boolean prop) is used instead
    // of `aria-hidden` when hidden: aria-hidden over a still-focusable subtree is a WCAG
    // violation (the ECE iframe stays in the tab order otherwise), whereas `inert` removes
    // the subtree from both the tab order and the a11y tree. Per the HTML spec, `inert`
    // only suppresses user-interaction/a11y — it does not stop the subtree from rendering
    // or executing scripts, so the ECE still loads and onReady still fires while hidden.
    <ButtonWrapper visible={visible} inert={!visible || undefined}>
      <ExpressCheckoutElement
        options={ECE_OPTIONS}
        onReady={event => {
          // Verified shape: for type === 'expressCheckout', @stripe/react-stripe-js
          // passes the raw ready event straight through (dist/react-stripe.js
          // createElementComponent: "Passes through the event, which includes
          // visible PM types"), and StripeExpressCheckoutElementReadyEvent
          // (node_modules/@stripe/stripe-js/types/stripe-js/elements/express-checkout.d.ts)
          // types `availablePaymentMethods` as `undefined | { applePay: boolean;
          // googlePay: boolean }`. Matches the brief's assumption exactly.
          const apm = event?.availablePaymentMethods
          onAvailabilityChange({ applePay: !!apm?.applePay, googlePay: !!apm?.googlePay })
        }}
        onConfirm={handleConfirm}
      />
    </ButtonWrapper>
  )
}

const PlatformPayButton = props => {
  const options = {
    mode: 'payment',
    amount: toCents(props.amountDollars), // CENTS for Elements init
    currency: 'usd',
    // Deferred Elements: these MUST match the server PaymentIntent that
    // stripe.confirmPayment reconciles against, or it rejects the confirmation.
    // Starbase's createPaymentIntent (services/stripe.js) sets:
    //   setup_future_usage: 'off_session'
    //   capture_method: 'manual' (authorize-only; captured server-side later)
    // and, by omitting automatic_payment_methods, defaults to payment_method_types:
    //   ['card'] — so Elements must declare ['card'] too (without it, Elements uses
    //   "automatic" PMs and confirmPayment refuses to reconcile). Apple Pay / Google
    //   Pay are card-based, so they still render under the 'card' type.
    setupFutureUsage: 'off_session',
    captureMethod: 'manual',
    paymentMethodTypes: ['card'],
  }
  return (
    <Elements stripe={stripePromise} options={options}>
      <PlatformPayInner {...props} />
    </Elements>
  )
}

export default PlatformPayButton
